import { FormControl, FormHelperText, IconButton, Input, InputAdornment } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useEffect, useRef, useState } from "react";

import { PencilRed } from "../../../icons";
import { validateExperienceTitle } from "../../../utils/validator";

const useStyles = makeStyles({
  title: {
    flexGrow: 1,
    color: "#fff",
    fontSize: "1.25rem",
    marginRight: "1rem",
    "@media (max-width:400px)": {
      fontSize: "0.875rem",
      marginRight: "0.5rem",
    },
  },
  underline: {
    "&:before": {
      borderBottom: "0px !important",
    },
    "&:hover:not($disabled):not($focused):not($error):before": {
      borderBottom: "0px !important",
    },
    "&:after": {
      borderBottom: "1px solid #71B6BC !important",
    },
  },
  disabled: {},
  focused: {},
  error: {},
  formHelperTextRoot: {
    top: "7px",
    position: "absolute",
    right: "-35px",
  },
});

export default function ExperienceTitle(props) {
  const { disabled, handleTitleUpdate, label = "", title = "" } = props;

  const classes = useStyles();
  const [createTitleValue, setCreateTitleValue] = useState(title || "");
  const [editingTitle, setEditingTitle] = useState(false);
  const [newTagError, setNewTagError] = useState("");

  const MAX_TITLE_LENGTH = 62;

  const timerId = useRef(null);
  useEffect(() => {
    return () => clearTimeout(timerId.current);
  }, []);

  useEffect(() => {
    setCreateTitleValue(title);
  }, [title]);

  const tagValue = createTitleValue;

  const validate = (value) => {
    const [message] = validateExperienceTitle(value);
    return message;
  };

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }

    setEditingTitle(false);
    clearTimeout(timerId.current);

    const errorMessage = validate(tagValue);
    setNewTagError(errorMessage);

    const titleTrimmed = title.trim();
    const newTitleTrimmed = tagValue.trim();

    if (!errorMessage) {
      if (titleTrimmed !== newTitleTrimmed) {
        handleTitleUpdate(newTitleTrimmed, label);
      }

      setCreateTitleValue(newTitleTrimmed);
    }
  };

  const handleBlur = async () => {
    const newTitle = tagValue.trim();

    if (!newTitle) {
      setCreateTitleValue(title);
      setNewTagError("");
    } else {
      await handleSubmit();
    }
  };

  const shouldBlur = (e) => {
    if (e.keyCode === 13) {
      e.target.blur();
    }
  };

  const handleChange = (e) => {
    e.persist();
    setCreateTitleValue(e.target.value);
    clearTimeout(timerId.current);

    if (!editingTitle) {
      const message = setEditingTitle(true);
      setNewTagError(message);
    }

    timerId.current = setTimeout(() => {
      const errorMessage = validate(e.target.value);
      setNewTagError(errorMessage);
    }, 300);
  };

  return (
    <form onSubmit={handleSubmit}>
      <FormControl>
        <Input
          disabled={disabled}
          onKeyDown={shouldBlur}
          classes={{
            underline: classes.underline,
            disabled: classes.disabled,
            focused: classes.focused,
            error: classes.error,
          }}
          margin="dense"
          value={createTitleValue || ""}
          onChange={handleChange}
          onBlur={handleBlur}
          className={classes.title}
          placeholder="Title"
          inputProps={{
            maxLength: MAX_TITLE_LENGTH,
            style: {
              backgroundColor: "transparent",
              padding: "3px 0",
            },
          }}
          endAdornment={
            <InputAdornment
              style={{
                backgroundColor: "transparent",
                display: editingTitle ? "flex" : "none",
              }}
              position="end"
            >
              <IconButton aria-label="update name" onClick={handleSubmit} size="small">
                <PencilRed className={classes.createNewTagSubmitIcon} htmlColor="#fff" />
              </IconButton>
            </InputAdornment>
          }
        />
        <FormHelperText
          className={[classes.createNewTagError, classes.listItem].join(" ")}
          classes={{ root: classes.formHelperTextRoot }}
          error={!!newTagError}
        >
          {newTagError}
        </FormHelperText>
      </FormControl>
    </form>
  );
}
